import { Box, Container, Stack, styled } from '@mui/material';
import { FilterParams } from '@synergy/models';
import { FormattedMessage } from 'react-intl';
import { PoolButton, PoolLink, PoolRow, PoolTypography, useIsMobile } from '@synergy/components-next';
import { ReactElement, ReactNode, useEffect, useMemo } from 'react';
import { SearchIcon } from '@synergy/theme-next';
import { useLocation, useSearchParams } from 'react-router-dom';

import { ArticleCard } from '../../components/article-card/article-card';
import { ArticleListItem } from '../../models/article-list-item';
import { AttributeFilterChips } from './attributes/attribute-filter-chips';
import { CategoryBar } from './category-bar/category-bar';
import { FilterChip, FilterChipParams } from '../../components/filter';
import { MinMaxPriceFilterChip } from './filter-chips/min-max-price/min-max-price-filter-chip';
import { PoolPagination } from '../../components/pagination/pagination';
import { ROUTES } from '../../utils/routes';
import { SearchBanner } from './search-banner/search-banner';
import { SearchOrigin } from '../../models/search-origin';
import { SearchPageEmptySearchparams } from './empty-states/search-page-empty-searchparams';
import { SearchPageEmptyState } from './empty-states/search-page-empty-state';
import { SearchPageFilters } from './filters/search-page-filters';
import { SupplierFilterChips } from './filter-chips/suppliers/supplier-filter-chips';
import { accountSelector, userSelector } from '../../store/configuration-selectors';
import {
    articleSearchResultReset,
    fetchArticleSearchResult,
    fetchInitialArticleSearchResult,
} from '../../store/articles-actions';
import { articleSearchResultsSelector } from '../../store/articles-selectors';
import { isLoadingSelector } from '../../store/feedback-selectors';
import { isSupplier } from '../../utils/is-supplier';
import { useAppDispatch, useAppSelector } from '../../../store/store';

const StyledBox = styled(Box)(({ theme }) => ({
    width: '100%',
    display: 'grid',
    gridTemplateColumns: `repeat(auto-fill, minmax(287px, 1fr))`,
    gap: theme.spacing(2),
    padding: theme.spacing(4, 0),

    [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(2, 0),
    },
}));

/**
 * The way on when a search failed: quiet, one line, and never in the way of the
 * results it stands above.
 */
const RelatedSearchBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1.5, 2),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.grey[100],

    '& > svg': {
        color: theme.palette.grey[600],
        flexShrink: 0,
    },
}));

const SearchResultContainer = styled(Container)(({ theme }) => ({
    [theme.breakpoints.down('sm')]: {
        padding: 0,
    },
}));

export const specialOfferFilters = [
    FilterParams.EcoProduct,
    FilterParams.BestPrice,
    FilterParams.MaxPrice,
    FilterParams.MinPrice,
    FilterParams.SortBy,
    FilterParams.SortDirection,
];
export const defaultFilters = [
    ...specialOfferFilters,
    FilterParams.SupplierIds,
    FilterParams.SpecialPrice,
    FilterParams.AttributeKeys,
];

interface LocationState {
    isSearchFromEnter?: boolean;
    searchOrigin?: SearchOrigin;
}

/**
 * A typed search, a page turn and a filter change all reload the same list. Telling
 * them apart is what keeps the search quality figures readable — a page turn is not
 * a new search, and a filter change means the first answer was not good enough.
 */
const searchOriginOf = (locationState: LocationState | undefined, params: URLSearchParams): SearchOrigin => {
    if (locationState?.searchOrigin) {
        return locationState.searchOrigin;
    }

    if (locationState?.isSearchFromEnter) {
        return 'search';
    }

    return Number(params.get(FilterParams.Page) ?? '1') > 1 ? 'paging' : 'refine';
};

export const SearchPage = (): ReactElement => {
    const dispatch = useAppDispatch();
    const articleSearchResults = useAppSelector(articleSearchResultsSelector);
    const searchIsLoading = useAppSelector(isLoadingSelector);
    const [searchParams, setSearchParams] = useSearchParams();
    const search = useMemo(() => searchParams.get(FilterParams.Search) ?? '', [searchParams]);
    const isMobile = useIsMobile();
    const isSpecialOffer = !!searchParams.get(FilterParams.SpecialOffer);
    const locationState = useLocation().state as LocationState;
    const user = useAppSelector(userSelector);
    const account = useAppSelector(accountSelector);

    // only for supplier to have the supplierId in the search params
    useEffect(() => {
        if (user && isSupplier(user) && account?.id) {
            setSearchParams(new URLSearchParams({ [FilterParams.SupplierIds]: account.id.toString() }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        dispatch(fetchInitialArticleSearchResult(searchParams.get(FilterParams.Search) ?? ''));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search, dispatch]);

    useEffect(() => {
        const hasParams = Array.from(searchParams).length > 0;
        if (articleSearchResults.total > 0 && !hasParams) {
            dispatch(articleSearchResultReset());
            return;
        }
        if (hasParams) {
            dispatch(
                fetchArticleSearchResult(
                    searchParams,
                    locationState?.isSearchFromEnter,
                    searchOriginOf(locationState, searchParams)
                )
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams, search, dispatch]);

    const activeFilters = isSpecialOffer ? specialOfferFilters : defaultFilters;

    const excludedFilterChipKeys = [
        FilterParams.MinPrice,
        FilterParams.MaxPrice,
        FilterParams.SortBy,
        FilterParams.SortDirection,
        FilterParams.AttributeKeys,
        FilterParams.SupplierIds,
    ];

    const filtersEnabled = Array.from(searchParams.keys()).some(key => activeFilters.includes(key as FilterParams));
    const filtersResetEnabled = Array.from(searchParams.keys())
        .filter(key => key !== FilterParams.SortBy && key !== FilterParams.SortDirection)
        .some(key => activeFilters.includes(key as FilterParams));

    const scrollToResults = (): void => {
        window.scrollTo(0, 0);
    };

    const handleReset = (): void => {
        activeFilters.forEach(filter => searchParams.delete(filter));
        searchParams.delete(FilterParams.Page);
        setSearchParams(searchParams);
    };

    // A search of its own rather than a filter on this one: what failed here has
    // nothing to do with the search somebody else got somewhere with.
    const relatedSearchTarget = {
        pathname: ROUTES.searchPage,
        search: new URLSearchParams({
            [FilterParams.Search]: articleSearchResults.relatedSearch ?? '',
        }).toString(),
    };

    const handleChipDelete = (key: string): void => {
        searchParams.delete(key);
        setSearchParams(searchParams);
    };

    const SearchResult = (): ReactElement | null => {
        if (articleSearchResults.total) {
            return (
                <StyledBox>
                    {articleSearchResults.searchResults.map((article: ArticleListItem, index: number) => (
                        <ArticleCard
                            key={article.id}
                            article={article}
                            orientation={isMobile ? 'landscape' : 'portrait'}
                            searchTracking={{
                                searchEventId: articleSearchResults.searchEventId,
                                position: index + 1,
                            }}
                        />
                    ))}
                </StyledBox>
            );
        }

        if (!searchIsLoading && Array.from(searchParams).length > 0) {
            return <SearchPageEmptyState />;
        }

        return <SearchPageEmptySearchparams />;
    };

    return (
        <>
            <SearchBanner />
            <CategoryBar />
            <Container maxWidth="xl" sx={{ mt: { xs: 4, sm: 5 } }}>
                <Stack gap={1.5}>
                    <Stack display="flex" flexDirection="row" justifyContent="space-between">
                        <SearchPageFilters filtersEnabled={filtersResetEnabled} />
                        {!isMobile && (
                            <PoolPagination
                                siblingCount={0}
                                pages={articleSearchResults.pages}
                                page={articleSearchResults.page}
                            />
                        )}
                    </Stack>
                    {!isMobile && filtersEnabled && (
                        <Stack direction="row" gap={1}>
                            <MinMaxPriceFilterChip />
                            {Array.from(searchParams.entries()).reduce<ReactNode[]>((acc, [key, value]) => {
                                if (
                                    activeFilters.includes(key as FilterParams) &&
                                    !excludedFilterChipKeys.includes(key as FilterParams)
                                ) {
                                    acc.push(
                                        <FilterChip
                                            key={key + value}
                                            filterParam={key as FilterChipParams}
                                            value={value}
                                            onChipDelete={() => handleChipDelete(key)}
                                        />
                                    );
                                }
                                return acc;
                            }, [])}
                            <AttributeFilterChips />
                            <SupplierFilterChips />
                            {filtersResetEnabled && (
                                <PoolButton variant="text" onClick={handleReset} sx={{ pt: 0, pb: 0, height: 28 }}>
                                    <FormattedMessage id="app.searchPage.filterReset" />
                                </PoolButton>
                            )}
                        </Stack>
                    )}
                </Stack>
            </Container>
            {articleSearchResults.relatedSearch && (
                <Container maxWidth="xl" sx={{ mt: 2 }}>
                    <RelatedSearchBox>
                        <SearchIcon />
                        <PoolTypography variant="body2">
                            <FormattedMessage
                                id={
                                    articleSearchResults.total
                                        ? 'app.searchPage.relatedSearch.nothingFitting'
                                        : 'app.searchPage.relatedSearch.nothingFound'
                                }
                                values={{
                                    term: searchParams.get(FilterParams.Search) ?? '',
                                    suggestion: (
                                        <PoolLink
                                            colorVariant="primary2"
                                            href={relatedSearchTarget}
                                            state={{ searchOrigin: 'related' }}
                                        >
                                            {articleSearchResults.relatedSearch}
                                        </PoolLink>
                                    ),
                                }}
                            />
                        </PoolTypography>
                    </RelatedSearchBox>
                </Container>
            )}
            <SearchResultContainer maxWidth="xl">
                <SearchResult />
            </SearchResultContainer>
            <Container maxWidth="xl" sx={{ mb: 10 }}>
                <PoolRow justifyContent="center">
                    <PoolPagination
                        siblingCount={1}
                        pages={articleSearchResults.pages}
                        page={articleSearchResults.page}
                        onChange={scrollToResults}
                    />
                </PoolRow>
            </Container>
        </>
    );
};
